import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/supabaseService';
import {
  INTERESTED_IN_OPTIONS,
  PERSONALITY_TRAITS,
  SEXUAL_PREFERENCES_HIRE_A_FRIEND,
  GENDER_OPTIONS,
} from '../constants/onboarding';

const STEPS = [
  { id: 1, title: 'Profile basics', icon: '👤' },
  { id: 2, title: 'Interests & preferences', icon: '✨' },
  { id: 3, title: 'Finish', icon: '🎉' },
];

const OnboardingPage = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    displayName: '',
    birthday: '',
    gender: '',
    city: '',
    interestedIn: [],
    personalityTraits: [],
    sexualPreferences: [],
  });

  useEffect(() => {
    if (!userProfile) return;
    if (userProfile.isMigratedUser) {
      navigate('/onboarding/recreate-profile', { replace: true });
      return;
    }
    setForm((prev) => ({
      ...prev,
      displayName: userProfile.displayName ?? userProfile.display_name ?? '',
      birthday: userProfile.birthday ?? '',
      gender: userProfile.gender ?? '',
      city: userProfile.city ?? '',
      interestedIn: Array.isArray(userProfile.interestedIn) ? userProfile.interestedIn : (userProfile.interested_in || []),
      personalityTraits: Array.isArray(userProfile.personalityTraits) ? userProfile.personalityTraits : (userProfile.personality_traits || []),
      sexualPreferences: Array.isArray(userProfile.sexualPreferences) ? userProfile.sexualPreferences : (userProfile.sexual_preferences || []),
    }));
  }, [userProfile, navigate]);

  if (userProfile?.isMigratedUser) {
    return null;
  }

  const toggleArray = (key, value) => {
    setForm((prev) => {
      const arr = prev[key] || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
  };

  const canProceedStep1 = form.birthday && form.gender;
  const canProceedStep2 = form.interestedIn.length > 0 && form.personalityTraits.length > 0 && form.sexualPreferences.length > 0;

  const handleFinish = async () => {
    setError('');
    setSaving(true);
    try {
      await profileService.setOnboardingCompleted(user.id, {
        displayName: form.displayName || undefined,
        birthday: form.birthday || null,
        gender: form.gender || null,
        city: form.city || null,
        interestedIn: form.interestedIn,
        personalityTraits: form.personalityTraits,
        sexualPreferences: form.sexualPreferences,
      });
      await refreshProfile();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white text-slate-900 rounded-2xl shadow-card border border-slate-200 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            {STEPS.map((s) => (
              <div
                key={s.id}
                className={`flex items-center gap-2 ${step === s.id ? 'text-primary font-semibold' : 'text-slate-400'}`}
              >
                <span>{s.icon}</span>
                <span className="hidden sm:inline">{s.title}</span>
              </div>
            ))}
          </div>
          <div className="h-2 bg-slate-200 rounded-full mb-6 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-xl text-slate-900">Profile basics</h2>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Display name</label>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="How you'd like to be called"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Birthday</label>
                  <input
                    type="date"
                    value={form.birthday}
                    onChange={(e) => setForm((p) => ({ ...p, birthday: e.target.value }))}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
                  <select
                    value={form.gender}
                    onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    {GENDER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="e.g. Mumbai"
                />
              </div>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="w-full py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-50"
              >
                Next: Interests & preferences
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-xl text-slate-900">Interests & preferences</h2>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Interested in</label>
                <div className="flex flex-wrap gap-3">
                  {INTERESTED_IN_OPTIONS.map((o) => (
                    <label key={o.value} className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.interestedIn.includes(o.value)}
                        onChange={() => toggleArray('interestedIn', o.value)}
                        className="rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span className="text-slate-700">{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Personality traits</label>
                <div className="flex flex-wrap gap-3">
                  {PERSONALITY_TRAITS.map((o) => (
                    <label key={o.value} className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.personalityTraits.includes(o.value)}
                        onChange={() => toggleArray('personalityTraits', o.value)}
                        className="rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span className="text-slate-700">{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Interests & preferences</label>
                <div className="flex flex-wrap gap-3">
                  {SEXUAL_PREFERENCES_HIRE_A_FRIEND.map((pref) => (
                    <label key={pref} className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.sexualPreferences.includes(pref)}
                        onChange={() => toggleArray('sexualPreferences', pref)}
                        className="rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span className="text-slate-700">{pref}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="flex-1 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-50"
                >
                  Next: Finish
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-xl text-slate-900 text-center">You're all set</h2>
              <p className="text-slate-600 text-center">
                Your profile is ready. Click below to go to your dashboard.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Go to dashboard'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
